import React, { useState, useEffect } from 'react';
import { Button } from './Button';
import { Lock, Users, TrendingUp, MousePointer, Download, ExternalLink, ArrowLeft, LogOut, AlertCircle } from 'lucide-react';
import { Logo } from './Logo';

interface DashboardData {
  overview: {
    visits: number;
    leads: number;
    conversion: string;
  };
  charts: {
    visitsByDay: { date: string; count: number }[];
  };
  leads: any[];
  sources: { utm_source: string; count: number }[];
}

interface AdminProps {
  onClose: () => void;
}

// Fallback Data for Preview/Offline Mode
const MOCK_DATA: DashboardData = {
  overview: {
    visits: 1243,
    leads: 84,
    conversion: "6.75"
  },
  charts: {
    visitsByDay: [
      { date: new Date(Date.now() - 6 * 86400000).toISOString(), count: 45 },
      { date: new Date(Date.now() - 5 * 86400000).toISOString(), count: 120 },
      { date: new Date(Date.now() - 4 * 86400000).toISOString(), count: 89 },
      { date: new Date(Date.now() - 3 * 86400000).toISOString(), count: 156 },
      { date: new Date(Date.now() - 2 * 86400000).toISOString(), count: 210 },
      { date: new Date(Date.now() - 1 * 86400000).toISOString(), count: 180 },
      { date: new Date().toISOString(), count: 243 }
    ]
  },
  leads: [
    { id: 101, name: "Lisa Müller", school_name: "Urban Burger", email: "lisa@example.com", package: "growth", timestamp: new Date().toISOString() },
    { id: 102, name: "Ahmet Yilmaz", school_name: "Yilmaz Barber", email: "ahmet@example.com", package: "premium", timestamp: new Date(Date.now() - 3600000).toISOString() },
    { id: 103, name: "Klaus Weber", school_name: "Weber Handwerk", email: "klaus@example.com", package: "starter", timestamp: new Date(Date.now() - 86400000).toISOString() },
  ],
  sources: [
    { utm_source: "instagram", count: 450 },
    { utm_source: "tiktok", count: 320 },
    { utm_source: "google", count: 150 },
    { utm_source: "linkedin", count: 80 },
  ]
};

export const Admin: React.FC<AdminProps> = ({ onClose }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  // Check session on mount
  useEffect(() => {
    const session = localStorage.getItem('zackig-admin-token');
    if (session) {
      setIsAuthenticated(true);
      fetchStats(session);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // 1. Try Real Backend
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      // Check if response is JSON (sometimes 404 returns HTML)
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const result = await res.json();
        if (result.success) {
          localStorage.setItem('zackig-admin-token', result.token);
          setIsAuthenticated(true);
          fetchStats(result.token);
          return;
        } else {
          setError('Passwort falsch.');
          setLoading(false);
          return;
        }
      }
      throw new Error("Backend not returning JSON");
    } catch (err) {
      // 2. Fallback: Client-Side Check (Preview Mode)
      // If backend fails (404 or network error), we check locally
      if (password === 'zackzack') {
        console.log("Backend unavailable, using offline mode");
        localStorage.setItem('zackig-admin-token', 'offline-demo-token');
        setIsOfflineMode(true);
        setIsAuthenticated(true);
        setData(MOCK_DATA); // Load Mock Data directly
      } else {
        setError('Passwort falsch (Offline Mode).');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (token: string) => {
    setLoading(true);
    
    if (token === 'offline-demo-token') {
      setIsOfflineMode(true);
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/admin/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const json = await res.json();
        setData(json);
      } else {
        // Token expired or invalid, or server 404
        throw new Error("Auth failed");
      }
    } catch (err) {
      console.log("Could not fetch stats, switching to demo data");
      setIsOfflineMode(true);
      setData(MOCK_DATA); // Fallback to mock data so UI doesn't break
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('zackig-admin-token');
    setIsAuthenticated(false);
    setData(null);
    setPassword('');
    setIsOfflineMode(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-brand-gray flex items-center justify-center px-4 relative z-50">
        {/* Back Button for Login Screen */}
        <button 
          onClick={onClose}
          className="absolute top-6 left-6 flex items-center gap-2 text-gray-500 hover:text-brand-orange transition font-bold text-sm"
        >
          <ArrowLeft size={18} />
          Zurück zur Website
        </button>

        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100 animate-fade-in-up">
          <div className="text-center mb-8 flex justify-center">
            <Logo variant="dark" className="h-10" />
            <span className="text-gray-400 text-lg font-normal ml-2 mt-auto mb-1">Internal</span>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2 text-gray-500">Passwort</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 p-4 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-brand-orange transition"
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>
            </div>
            {error && (
              <div className="flex items-center gap-2 text-red-500 text-sm font-bold bg-red-50 p-3 rounded-lg">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            <Button fullWidth variant="primary" type="submit" disabled={loading}>
              {loading ? 'Lade...' : 'Login'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-brand-orange font-bold z-50 relative">Lade Daten...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans relative z-50">
      {/* Integrated Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 px-4 md:px-8 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <Button 
              variant="outline" 
              onClick={onClose} 
              className="!py-2 !px-4 flex items-center gap-2 text-sm"
            >
              <ArrowLeft size={16} />
              <span className="hidden md:inline">Zur Startseite</span>
            </Button>
            <div className="flex items-center gap-2">
                <Logo variant="dark" className="h-8" />
                <span className="text-gray-400 font-bold">Admin</span>
            </div>
            {isOfflineMode && (
              <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full border border-yellow-200">
                Demo Mode (Offline)
              </span>
            )}
          </div>
          
          <button 
            onClick={handleLogout} 
            className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-red-500 transition"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Total Views</p>
                <h3 className="text-3xl font-black text-brand-dark">{data.overview.visits}</h3>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-blue-600"><MousePointer size={20} /></div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Total Leads</p>
                <h3 className="text-3xl font-black text-brand-dark">{data.overview.leads}</h3>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-green-600"><Users size={20} /></div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Conversion Rate</p>
                <h3 className="text-3xl font-black text-brand-dark">{data.overview.conversion}%</h3>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg text-brand-orange"><TrendingUp size={20} /></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Leads Table */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-bold text-brand-dark">Neueste Leads</h3>
              <Button variant="outline" className="!py-1 !px-3 !text-xs flex items-center gap-2">
                <Download size={14} /> CSV
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                  <tr>
                    <th className="px-6 py-3">Datum</th>
                    <th className="px-6 py-3">Name</th>
                    <th className="px-6 py-3">Firma</th>
                    <th className="px-6 py-3">Paket</th>
                    <th className="px-6 py-3">CRM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.leads.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-gray-400">Keine Leads bisher.</td></tr>
                  ) : (
                    data.leads.map((lead: any) => (
                      <tr key={lead.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 text-gray-500 whitespace-nowrap">
                          {new Date(lead.timestamp).toLocaleDateString('de-DE')} <span className="text-xs block md:inline">{new Date(lead.timestamp).toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'})}</span>
                        </td>
                        <td className="px-6 py-4 font-bold text-brand-dark">
                          {lead.name}
                          <div className="text-xs font-normal text-gray-400 truncate max-w-[150px]">{lead.email}</div>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{lead.school_name}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${
                            lead.package === 'premium' ? 'bg-purple-100 text-purple-700' :
                            lead.package === 'growth' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {lead.package}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <a 
                            href={`https://odoo.meinfahrer.app/odoo/crm/${lead.id}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-brand-orange hover:text-orange-700 font-bold flex items-center gap-1 hover:underline"
                          >
                            Odoo <ExternalLink size={14} />
                          </a>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Traffic Sources */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-bold text-brand-dark mb-6">Traffic Quellen</h3>
            <div className="space-y-4">
              {data.sources.length === 0 ? (
                <p className="text-gray-400 text-sm">Keine Daten.</p>
              ) : (
                data.sources.map((source, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-gray-700">{source.utm_source || 'Direkt / Unbekannt'}</span>
                      <span className="text-gray-500">{source.count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div 
                        className="bg-brand-orange h-2 rounded-full" 
                        style={{ width: `${(source.count / data.overview.visits) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Simple Chart Visualization */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="font-bold text-brand-dark mb-6">Besucher (7 Tage)</h3>
          <div className="flex items-end justify-between h-48 gap-2 border-b border-gray-100 pb-2">
             {data.charts.visitsByDay.map((day, idx) => (
               <div key={idx} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
                 <div 
                    className="w-full max-w-[50px] bg-brand-orange rounded-t-md hover:opacity-80 transition relative group flex items-end justify-center"
                    style={{ 
                      height: `${Math.max(5, (day.count / (Math.max(...data.charts.visitsByDay.map(d => d.count)) || 1)) * 100)}%`,
                      opacity: day.count === 0 ? 0.3 : 1
                    }}
                 >
                   <div className="mb-2 text-white font-bold text-xs hidden md:block drop-shadow-md">
                     {day.count > 0 ? day.count : ''}
                   </div>
                   
                   {/* Tooltip */}
                   <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-brand-dark text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap pointer-events-none z-10">
                     {day.count} Besuche
                   </div>
                 </div>
                 <span className="text-xs text-gray-500 font-medium">
                   {new Date(day.date).toLocaleDateString('de-DE', { weekday: 'short' }).slice(0, 2)}
                 </span>
               </div>
             ))}
          </div>
        </div>

      </main>
    </div>
  );
};